import { observable, action, makeObservable } from 'mobx';
import _ from 'lodash';

import agent from '../agent';

import userStore from './userStore';
import { getDistanceZone } from '../../utils/commonMethods';

let addressThrottleTimer = null;

class TaskStore {
    constructor() {
        makeObservable(this, {
            currentList: observable,
            currentEntity: observable,
            taskCounts: observable,
            taskTotalHours: observable,
            updatingErrors: observable,
            updating: observable,
            loading: observable,
            requestParams: observable,
            deleteSuccess: observable,
            currentTasksLookup: observable,
            loadingLookup: observable,
            lastListLoadTime: observable,
            gpsNowLoading: observable,
            resetLastListLoadTime: action,
            filters: observable,
            appliedFilters: observable,
            setFilter: action,
            onFilter: action,
            loadList: action,
            returnDefaultNew: action,
            load: action,
            save: action,
            remove: action,
            loadLookup: action,
            performRecalcGpsForTask: action,
            recalcGpsForTask: action,
            loadTasksForCloning: action,
            cloneTasks: action,
            saveIntoIntegration: action,
            prependTaskToLookup: action,
            loadMembersForTask: action,
            getTaskTotals: action,
            getTaskTotalHours: action,
            toggleTaskFavorite: action,
            saveTaskMembers: action,
            getTaskMembersFromProjectMembers: action,
            getTaskMembersFromProjectCount: action,
            lastInteractedId: observable,
            setLastInteractedId: action,
            clearLastInteractedId: action,
            getTasksFromIntegration: action,
            saveTasksFromIntegrationTripletex: action,
        });
    }

    lastInteractedId = null;

    setLastInteractedId(id) {
        this.lastInteractedId = id ?? null;
    }

    clearLastInteractedId() {
        this.lastInteractedId = null;
    }

    currentList = {};
    currentEntity = {};
    taskCounts = {
        all: 0,
        active: 0,
        inactive: 0
    };
    taskTotalHours = {};
    updatingErrors = null;
    updating = false;
    loading = false;
    requestParams = null;
    deleteSuccess = false;
    currentTasksLookup = {
        rows: [],
        pages: 0
    };
    loadingLookup = false;
    lastListLoadTime = null;
    gpsNowLoading = false;
    filters = {
        status: 'active',
        name: '',
        project: '',
    };
    appliedFilters = {
        status: 'active',
        name: '',
        project: '',
    };

    resetLastListLoadTime() {
        this.lastListLoadTime = new Date();
    }

    setFilter(name, value) {
        const filters = Object.assign({}, this.filters);
        filters[name] = value;
        this.filters = filters;
    }

    onFilter() {
        this.appliedFilters = Object.assign({}, this.filters);
    }

    loadList(params) {
        return agent.Tasks.list(params)
            .then(
                action(list => {
                    this.requestParams = params;
                    list.time = new Date();
                    this.lastListLoadTime = list.time;
                    this.currentList = list;
                    return list;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    returnDefaultNew(currentUser) {
        this.currentEntity = {
            task: {
                assignee_id: currentUser && currentUser.user_type && currentUser.user_type === 'member' ? `${currentUser.id}` : '',
                name: null,
                data: {},
                start_time: 8 * 60,
                end_time: 17 * 60,
                scheduled_days: '0,1,2,3,4',
            },
        };
        this.loading = false;
    }

    load(id) {
        this.loading = true;
        return agent.Tasks.load(id)
            .then(
                action(response => {
                    if (response.task && !response.task.data) response.task.data = {};
                    this.currentEntity = response;
                    this.loading = false;
                    return response;
                })
            )
            .catch(
                action(err => {
                    this.loading = false;
                    throw err;
                })
            );
    }

    save(values, isAdd) {
        this.updating = true;
        return agent.Tasks.save(values, isAdd)
            .then(
                action(user => {
                    this.currentEntity = user;
                    return user;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }

    async remove(id, isSelf) {
        await agent.Tasks.remove(id, isSelf);
        this.deleteSuccess = true;
        return 1;
    }

    loadLookup(projectId = undefined, userId = undefined, name = undefined, page = 0, sortByDefaultTask = false, fromAdditionalPaymentsForm = false, filterType = 'all', taskIds = []) {
        this.loadingLookup = true;
        const extraPaymentsParams = { name, page, filterType };
        if (projectId) extraPaymentsParams.project_id = projectId;
        if (filterType !== 'all' && taskIds && taskIds.length) {
            extraPaymentsParams.taskIds = taskIds.join(',');
        }
        const params = fromAdditionalPaymentsForm ? extraPaymentsParams : {
            name,
            userId,
            page,
            sortByDefaultTask
        }
        const apiFn = fromAdditionalPaymentsForm ? agent.Clients.getActiveTaskWithMembersForExtraPayments : agent.Tasks.lookupByName;
        return apiFn(projectId, params)
            .then(
                action(list => {
                    if (page === 0) {
                        this.currentTasksLookup.rows = list.rows;
                    } else {
                        this.currentTasksLookup.rows = [...this.currentTasksLookup.rows, ...list.rows];
                    }
                    this.currentTasksLookup.pages = list.pages;
                    this.loadingLookup = false;
                    return list.rows;
                })
            )
            .catch(
                action(err => {
                    this.loadingLookup = false;
                    throw err;
                })
            )
    }

    //task member lookup
    loadMembersForTask(taskId, isNotPagination = undefined) {
        if (!taskId || !parseInt(taskId)) return;
        this.loadingLookup = true;
        const params = { isNotPagination };
        return agent.Tasks.loadMembersForTask(taskId, params)
            .then(
                action(list => {
                    this.loadingLookup = false;
                    return list;
                })
            )
            .catch(
                action(err => {
                    this.loadingLookup = false;
                    throw err;
                })
            )
    }

    async performRecalcGpsForTask(newAddress, userInsteadOfTask, commonStore, t, taskId) {
        this.gpsNowLoading = true;
        let tsk;
        if (!userInsteadOfTask) {
            this.currentEntity.task.data.addressCache = newAddress;
            tsk = _.cloneDeep(this.currentEntity.task);
        } else {
            userStore.currentEntity.user.data.addressCache = newAddress;
            tsk = _.cloneDeep(userStore.currentEntity.user);
        }
        try {
            if (commonStore?.config?.client?.calculate_task_distance_with_company && !commonStore.config.client?.gps_data) {
                commonStore.addNotification(t('Please add the company address under company profile'), null, 'warning');
            }
            const data = await agent.Tasks.gpsAddress(newAddress, userInsteadOfTask ? null : taskId, !userInsteadOfTask);
            tsk.gps_data = data.result;
            tsk.data.addressCache = newAddress;
            tsk.distance_from_company_location = data.taskDistanceDetails;

            if (commonStore?.config?.client?.calculate_task_distance_with_company) {
                let getZone = ''
                if (tsk.distance_from_company_location) {
                    const distance = tsk.distance_from_company_location.split(' ')[0]
                    getZone = getDistanceZone(parseFloat(distance), commonStore.config.client.data.extendedRules.kilometersRules)
                    tsk.distance_type = getZone.distance;
                }
            }

            if (!userInsteadOfTask) {
                this.currentEntity = { task: tsk };
            } else {
                userStore.currentEntity = { user: tsk };
            }
            this.gpsNowLoading = false;
        } catch (e) {
            this.gpsNowLoading = false;
            throw e;
        }
    }

    async recalcGpsForTask(changes, userInsteadOfTask, commonStore, t) {
        const entityToAddressString = e => {
            return `${e.address}, ${e.post_place}, ${e.post_number}`;
        };

        if (!changes.address || !changes.post_place) {
            return changes;
        }

        const newAddress = entityToAddressString(changes);

        if (!userInsteadOfTask) {
            if (newAddress === this.currentEntity.task.data.addressCache) return changes;
        } else if (newAddress === userStore.currentEntity.user.data.addressCache) return changes;

        addressThrottleTimer = new Date().getTime();
        setTimeout(() => {
            // throttle
            if (new Date().getTime() - addressThrottleTimer < 1000) {
                return;
            }
            if (!userInsteadOfTask) {
                if (this.currentEntity.task.data.addressCache === newAddress) {
                    return;
                }
            } else if (userStore.currentEntity.user.data.addressCache === newAddress) {
                return;
            }

            this.performRecalcGpsForTask(newAddress, userInsteadOfTask, commonStore, t, changes.id ? changes.id : null);
        }, 1000);
        return changes;
    }

    async loadTasksForCloning(id) {
        return agent.Tasks.getTaskToClone(id).then(
            action(list => {
                return list;
            })
        )
            .catch(
                action(err => {
                    throw err;
                })
            )
    }

    async cloneTasks(data) {
        return agent.Tasks.cloneTasks(data).then(
            action(list => {
                return list;
            })
        )
            .catch(
                action(err => {
                    throw err;
                })
            )
    }
    saveIntoIntegration(values) {
        return agent.Tasks.saveIntoIntegration(values)
            .then(
                action(task => {
                    return task;
                })
            )
            .catch(
                action(err => {
                    this.updating = false;
                    this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                    throw err;
                })
            );
    }

    saveTaskFromIntegration(data) {
        return agent.Tasks.saveTaskFromIntegration(data).then(
            action(res => {
                return res;
            })
        ).catch(
            action(err => {
                this.updating = false;
                this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                throw err;
            })
        )
    }

    saveTaskInBulkFromIntegration(data) {
        return agent.Tasks.saveTaskInBulkFromIntegration(data).then(
            action(res => {
                return res;
            })
        ).catch(
            action(err => {
                this.updating = false;
                this.updatingErrors = err.response && err.response.body && err.response.body.errors;
                throw err;
            })
        )
    }



    prependTaskToLookup(task) {
        if (!this.currentTasksLookup) {
            this.currentTasksLookup.rows = [];
        }
        const exists = this.currentTasksLookup?.rows.find(t => t.id === task.id);
        if (!exists) {
            this.currentTasksLookup.rows = [task, ...this.currentTasksLookup.rows];
        }
    }

    getTaskTotals() {
        return agent.Tasks.getTaskTotals()
            .then(
                action(response => {
                    const { all, active, inactive } = response;
                    this.taskCounts.all = all;
                    this.taskCounts.active = active;
                    this.taskCounts.inactive = inactive;
                    return response;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    getTaskTotalHours(id, params) {
        return agent.Tasks.getTaskTotalHours(id, params)
            .then(
                action(response => {
                    this.taskTotalHours[id] = response ? response : 0;
                    return true;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    toggleTaskFavorite(taskId) {
        return agent.Tasks.toggleTaskFavorite(taskId)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveTaskMembers(values) {
        return agent.Tasks.saveTaskMembers(values)
            .then(
                action(list => {
                    return list;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

    getTaskMembersFromProjectMembers(projectId, taskId, options = {}) {
        if (!projectId || !taskId) return;
        const { name = '', assigned = 'all', page = 0, pagesize, isNotPagination = false, pmMembers } = options;
        const params = { projectId, taskId, name, assigned, page, pagesize, isNotPagination: isNotPagination ? true : '', pmMembers };
        return agent.Tasks.getTaskMembersFromProjectMembers(params)
            .then(
                action(res => {
                    return res;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            );
    }

    getTaskMembersFromProjectCount(projectId, taskId, pmMembers) {
        if (!projectId || !taskId) return;
        const params = { projectId, taskId, pmMembers };
        return agent.Tasks.getTaskMembersFromProjectCount(params)
            .then(
                action(res => {
                    return res;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            );
    }

    getTasksFromIntegration(params) {
        return agent.Tasks.getTasksFromIntegration(params)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    saveTasksFromIntegrationTripletex(data) {
        return agent.Tasks.saveTasksFromIntegrationTripletex(data)
            .then(
                action(res => {
                    return res;
                })
            )
            .catch(
                action(err => {
                    throw err;
                })
            );
    }

    deleteTaskMember(userId, taskId) {
        if (!userId || !taskId) return;
        return agent.Tasks.deleteTaskMember({ userId, taskId })
            .then(
                action(res => {
                    return res;
                })
            ).catch(
                action(err => {
                    throw err;
                })
            )
    }

}
const _TaskStore = new TaskStore();
export default _TaskStore;
